import { api } from './apiUtils';
import moment from 'moment';
import { AggregationType } from './chartUtils';

// --- Types ---

export interface CurrentAdData {
  doi: {
    type: 'webinar' | 'plx' | 'empty';
    product_name?: string;
    concept?: string;
    product_id?: string;
    start_date?: string;
    landing_url?: string;
    banner_pattern?: string;
  };
  doi_skip_reason?: string | null;
  article: {
    type: 'webinar' | 'plx' | 'empty';
    product_name?: string;
    concept?: string;
    product_id?: string;
    landing_url?: string;
    banner_pattern?: string;
    cached_copy?: {
      headline: string;
      body: string;
      cta_text: string;
      generated_at: string;
      prompt_id: string | null;
      model_used: string | null;
    } | null;
  };
}

export interface ConversionRow {
  period: string;
  doi_webinar_regs: number;
  doi_webinar_attendees: number;
  doi_plx_orders: number;
  doi_plx_revenue: number;
  article_webinar_regs: number;
  article_webinar_attendees: number;
  article_plx_orders: number;
  article_plx_revenue: number;
}

export interface ConversionTotals {
  doi_webinar_regs: number;
  doi_webinar_attendees: number;
  doi_plx_orders: number;
  doi_plx_revenue: number;
  article_webinar_regs: number;
  article_webinar_attendees: number;
  article_plx_orders: number;
  article_plx_revenue: number;
}

export interface ProductRow {
  placement: string;
  product_type: string;
  concept: string;
  product_name: string | null;
  total_conversions: number;
  total_attendees: number;
  total_revenue: number;
  first_conversion: string;
  last_conversion: string;
}

export interface AdCopyRow {
  product_type: string;
  product_id: string;
  product_name: string | null;
  concept: string | null;
  copy_id: number;
  version: number;
  source_copy_hash: string | null;
  headline: string;
  body: string;
  cta_text: string;
  generated_at: string;
  prompt_id: string | null;
  model_used: string | null;
  // All-time per-product (includes pre-2026-06-08 banners without copy_id suffix)
  registrations: number | null;
  attendees: number | null;
  orders: number | null;
  cpa_revenue: number | null;
  // Per-variant — only conversions whose banner carries this row's `_c{copy_id}v{version}` suffix
  variant_registrations: number | null;
  variant_attendees: number | null;
  variant_orders: number | null;
  variant_cpa_revenue: number | null;
}

export interface RegeneratedCopy {
  headline: string;
  body: string;
  cta_text: string;
}

// --- DOI A/B Test types ---

export interface DoiABTestVariant2Summary {
  impressions: number;
  unique_emails: number;
  vnr_leads: number;
  vnr_revenue: number;
  revenue_per_impression: number;
}

export interface DoiABTestVariant5Summary {
  impressions: number;
  unique_emails: number;
  webinar_regs: number;
  webinar_attendees: number;
  webinar_orders: number;
  webinar_revenue: number;
  plx_orders: number;
  plx_revenue: number;
  total_revenue: number;
  revenue_per_impression: number;
}

export interface DoiABTestSummary {
  variant_2: DoiABTestVariant2Summary;
  variant_5: DoiABTestVariant5Summary;
  avg_cpa_per_registration: number;
}

export interface DoiABTestTimelineRow {
  period: string;
  v2_impressions: number;
  v2_unique_emails: number;
  v5_impressions: number;
  v5_unique_emails: number;
  v2_vnr_leads: number;
  v2_vnr_revenue: number;
  v5_webinar_regs: number;
  v5_webinar_attendees: number;
  v5_webinar_orders: number;
  v5_webinar_revenue: number;
  v5_plx_orders: number;
  v5_plx_revenue: number;
}

export interface DoiABTestData {
  summary: DoiABTestSummary;
  timeline: DoiABTestTimelineRow[];
}

// --- Fetch functions ---

export async function fetchCurrentAds(): Promise<CurrentAdData> {
  const response = await api.get('/api/ad-evaluation/current');
  return response.data.data;
}

export async function fetchConversions(
  startDate: Date,
  endDate: Date,
  aggregation: AggregationType,
): Promise<{ timeline: ConversionRow[]; totals: ConversionTotals }> {
  const params = new URLSearchParams({
    start_date: moment(startDate).format('YYYY-MM-DD'),
    end_date: moment(endDate).format('YYYY-MM-DD'),
    aggregation,
  });
  const response = await api.get(`/api/ad-evaluation/conversions?${params}`);
  return response.data.data;
}

export async function fetchProducts(startDate: Date, endDate: Date): Promise<ProductRow[]> {
  const params = new URLSearchParams({
    start_date: moment(startDate).format('YYYY-MM-DD'),
    end_date: moment(endDate).format('YYYY-MM-DD'),
  });
  const response = await api.get(`/api/ad-evaluation/products?${params}`);
  return response.data.data;
}

export async function fetchAdCopy(): Promise<{
  rows: AdCopyRow[];
  avgCpaPerRegistration: number;
}> {
  const response = await api.get('/api/ad-evaluation/ad-copy');
  return {
    rows: response.data.data,
    avgCpaPerRegistration: Number(response.data.avg_cpa_per_registration) || 0,
  };
}

export async function fetchDoiABTest(
  startDate: string,
  endDate: string,
  aggregation: AggregationType,
): Promise<DoiABTestData> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    aggregation,
  });
  const response = await api.get(`/api/ad-evaluation/doi-ab-test?${params}`);
  return response.data.data;
}

export async function regenerateAdCopy(
  productType: string,
  productId: string,
): Promise<RegeneratedCopy> {
  const response = await api.post('/api/ad-evaluation/ad-copy/regenerate', {
    product_type: productType,
    product_id: productId,
  });
  return response.data.data;
}
